import {
  Action,
  Card,
  CardSkeleton,
  Grid,
  ICON_ALERT_CIRCLE_OUTLINE,
  ICON_IMAGE_EDIT_OUTLINE,
  Message,
  Spacing,
  Stack,
  Text,
} from '@doveit/bricks';
import React from 'react';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';
import { useSearchBrandTemplatesInfinite } from '../../hooks/use-search-brand-templates/useSearchBrandTemplates';
import SearchByQueryAction from '../../../components/search-by-query-action/SearchByQueryAction';
import useFilters from '../../../hooks/use-filters/useFilters';
import CanvaModelCard from '../../components/canva-model-card/CanvaModelCard';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { goToPage } from '../../../utils/navigate/utils';
import { CANVA_BRAND_TEMPLATES_URL } from '../../constants';

const BrandTemplateListPage: React.FC = () => {
  const isMobile = useIsDevice('mobile');

  const {
    replaceFilters,
    filters,
  } = useFilters({
    query: (v) => v,
  });

  const {
    data: brandTemplates,
    isLoading: areBrandTemplatesLoading,
    isLoadingMore: areMoreBrandTemplatesLoading,
    error: brandTemplatesError,
    hasMore: hasMoreBrandTemplates,
    loadMore: loadMoreBrandTemplates,
  } = useSearchBrandTemplatesInfinite(filters.query);

  return (
    <SimplePageLayout>
      <Card paddingSize="M">
        {brandTemplatesError ? (
          <Card.Content>
            <Message
              message="Non è stato possibile caricare i template Canva"
              description={<Text.BodySmall>Prova ad aggiornare la pagina, o visualizzali direttamente su Canva.</Text.BodySmall>}
              icon={ICON_ALERT_CIRCLE_OUTLINE}
              type="critical"
              actions={[
                <Action
                  aria-label="Visualizza i template su Canva"
                  label="Visualizza su Canva"
                  color="critical"
                  emphasis="high"
                  size="S"
                  onClick={() => goToPage(CANVA_BRAND_TEMPLATES_URL)}
                />,
              ]}
            />
          </Card.Content>
        ) : (
          <>
            <Card.Header
              icon={{ path: ICON_IMAGE_EDIT_OUTLINE }}
              title="Template Canva"
            />
            <Card.Content>
              <Stack gap={300}>
                <Text.Body>
                  Usa il template Canva di tuo interesse e personalizzalo in pochi click.
                </Text.Body>

                <SearchByQueryAction
                  variant="input"
                  width="30rem"
                  initialQuery={filters.query}
                  onSearch={(q) => replaceFilters({ query: q })}
                />
              </Stack>
            </Card.Content>

            <Card.Content>
              {!areBrandTemplatesLoading && !areMoreBrandTemplatesLoading && !brandTemplatesError && brandTemplates.length === 0 && (
                <Message message="Nessun template Canva trovato." />
              )}

              {brandTemplates.length > 0 && (
                <Grid gutter={150} aria-label="Lista template Canva">
                  {brandTemplates.map((template) => (
                    <Grid.Unit
                      key={template.id}
                      size={{
                        XS: 1, SM: 1 / 2, MD: 1 / 3, LG: 1 / 4, XL: 1 / 5,
                      }}
                    >
                      <CanvaModelCard brandTemplate={template} />
                    </Grid.Unit>
                  ))}
                </Grid>
              )}

              {(areBrandTemplatesLoading || areMoreBrandTemplatesLoading) && (
                <Spacing margin={[150, 0]}>
                  <Grid gutter={150} aria-label="Template Canva in caricamento">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <Grid.Unit
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        size={{
                          XS: 1, SM: 1 / 2, MD: 1 / 3, LG: 1 / 4, XL: 1 / 5,
                        }}
                      >
                        <CardSkeleton />
                      </Grid.Unit>
                    ))}
                  </Grid>
                </Spacing>
              )}
            </Card.Content>

            {hasMoreBrandTemplates && (
              <Card.Content>
                <Stack alignItems="center">
                  <Action
                    label="Carica altri"
                    aria-label="Carica altri template Canva"
                    onClick={loadMoreBrandTemplates}
                    loading={areMoreBrandTemplatesLoading}
                    size={isMobile ? 'S' : 'M'}
                  />
                </Stack>
              </Card.Content>
            )}
          </>
        )}
      </Card>
    </SimplePageLayout>
  );
};

export default BrandTemplateListPage;
